/**
 * Sales → Media Plans. Owns the plan list and shows, by priority, the plan
 * the URL points at (?plan=<id>), the builder (?new=1, or ?draft=<id> for a
 * brief priced in Culture Genius chat), or the list. The reference data and
 * advertiser registry come from the dashboard (useMediaPlanRegistry, shared
 * with Rate Cards); when they fail to load the list still works and an inline
 * banner offers Retry. The dashboard keeps this tab mounted once visited.
 *
 * The builder's draft autosaves in this browser, one per user: opening the
 * builder picks it back up, and the list offers to resume it. A builder
 * seeded from somewhere else (a new version, a chat draft) asks before it
 * replaces a different unsaved draft.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '../../ui';
import { getMediaPlan, getMediaPlanDraft, listMediaPlans } from '../../../services/mediaPlansApi';
import { currentUserUid } from '../../../services/api';
import PlanBuilder, { type BuilderBoot, type Notify } from './PlanBuilder';
import PlanDetail, { type PlanDetailSeed } from './PlanDetail';
import PlanList from './PlanList';
import InlineConfirm from './InlineConfirm';
import type { CreatePlanResult, MediaPlanDoc, MediaPlanDraftDoc } from './types';
import type { MediaPlanRegistry } from './useMediaPlanRegistry';
import { draftFromChatDraft, draftFromPlan, newDraft } from './planDraft';
import { clearStoredDraft, loadStoredDraft, storedDraftTitle, type StoredDraft } from './draftStorage';
import { bannerStyle, fmtBudget, fmtDateTime } from './formStyles';

export type MediaPlansMode = 'list' | 'builder';
export type MediaPlansTarget = { view: 'list' } | { view: 'builder' } | { view: 'plan'; planId: string };
export type MediaPlansNavigate = (target: MediaPlansTarget, opts?: { replace?: boolean }) => void;

/** The list fetch cap — the backend's maximum `limit`. */
export const PLAN_LIST_LIMIT = 200;

interface Props {
  active: boolean;
  planId: string | null;
  mode: MediaPlansMode;
  /** ?draft=<id>: a chat draft to open in the builder. */
  chatDraftId?: string | null;
  registry: MediaPlanRegistry;
  onNavigate: MediaPlansNavigate;
  canManagePlans: boolean;
  notify: Notify;
}

type Seed = Omit<BuilderBoot, 'key' | 'restoredAt' | 'replaces' | 'chatDraftId'>;

const errorMessage = (err: unknown): string => (err instanceof Error ? err.message : 'Something went wrong.');

/** "State Farm v1 ($250K / $350K)" */
export const revisionLabel = (plan: MediaPlanDoc): string => `${plan.advertiser.name} v${plan.version} (${plan.tiers.map((t) => fmtBudget(t.budgetUsd)).join(' / ')})`;

const restoredBoot = (key: number, stored: StoredDraft): BuilderBoot => ({
  key,
  draft: stored.draft,
  revisesPlanId: stored.revisesPlanId,
  revisesLabel: stored.revisesLabel,
  chatDraftId: stored.chatDraftId,
  restoredAt: stored.savedAt,
});

const MediaPlansTab: React.FC<Props> = ({ active, planId, mode, chatDraftId = null, registry, onNavigate, canManagePlans, notify }) => {
  const [plans, setPlans] = useState<MediaPlanDoc[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [plansLoadedAt, setPlansLoadedAt] = useState<number | null>(null);
  const [mine, setMine] = useState(false);
  const [boot, setBoot] = useState<BuilderBoot | null>(null);
  const [detailSeed, setDetailSeed] = useState<PlanDetailSeed | null>(null);
  const [stored, setStored] = useState<StoredDraft | null>(null);
  const [confirmDiscardStored, setConfirmDiscardStored] = useState(false);
  const [chatImport, setChatImport] = useState<{ id: string; error?: string } | null>(null);
  const bootCount = useRef(0);
  const plansGeneration = useRef(0);
  const uid = currentUserUid();
  const advertisersRef = useRef(registry.advertisers);
  advertisersRef.current = registry.advertisers;
  const mounted = useRef(true);
  // Set on every mount: StrictMode runs this cleanup once on its simulated unmount in development.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadPlans = useCallback(async () => {
    const gen = ++plansGeneration.current;
    setPlansLoading(true);
    setPlansError(null);
    try {
      const list = await listMediaPlans({ mine, limit: PLAN_LIST_LIMIT });
      if (!mounted.current || gen !== plansGeneration.current) return;
      setPlans(list);
      setPlansLoadedAt(Date.now());
    } catch (err) {
      if (mounted.current && gen === plansGeneration.current) setPlansError(errorMessage(err));
    } finally {
      if (mounted.current && gen === plansGeneration.current) setPlansLoading(false);
    }
  }, [mine]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  /** How a builder opens from `seed` (or from nothing): this browser's saved draft wins when it came from the same place. */
  const bootFor = useCallback(
    (seed: Seed | null): BuilderBoot => {
      const key = ++bootCount.current;
      const saved = loadStoredDraft(uid);
      if (!seed) return saved ? restoredBoot(key, saved) : { key, draft: newDraft() };
      const sameSource = !!saved && (seed.chatDraft ? saved.chatDraftId === seed.chatDraft.id : !!seed.revisesPlanId && saved.revisesPlanId === seed.revisesPlanId && !saved.chatDraftId);
      if (saved && sameSource) return { ...restoredBoot(key, saved), chatDraft: seed.chatDraft };
      return { key, ...seed, replaces: saved ?? undefined };
    },
    [uid],
  );

  // Entering the builder with nothing seeded: pick up the saved draft, or start blank. Leaving drops the boot.
  useEffect(() => {
    if (mode !== 'builder') {
      setBoot(null);
      return;
    }
    if (chatDraftId) return;
    setBoot((current) => current ?? bootFor(null));
  }, [mode, chatDraftId, bootFor]);

  // A detail seed is for the plan page it was made for, so reopening a plan fetches it fresh.
  useEffect(() => {
    setDetailSeed((seed) => (seed && seed.plan.id !== planId ? null : seed));
  }, [planId]);

  // The list offers the saved draft back.
  useEffect(() => {
    if (mode === 'list' && !planId) {
      setStored(loadStoredDraft(uid));
      setConfirmDiscardStored(false);
    }
  }, [mode, planId, uid]);

  // ?draft=<id>: load the chat draft (once the registry is in), then open it as ?new=1.
  const referenceLoaded = !!registry.reference;
  const agencies = registry.reference?.agencies.agencies;
  useEffect(() => {
    if (!chatDraftId) {
      setChatImport(null);
      return;
    }
    if (!referenceLoaded || !agencies) return;
    let cancelled = false;
    setChatImport({ id: chatDraftId });
    (async () => {
      try {
        const doc: MediaPlanDraftDoc | null = await getMediaPlanDraft(chatDraftId);
        if (cancelled) return;
        if (!doc) {
          setChatImport({ id: chatDraftId, error: 'This chat draft wasn’t found or has expired — chat drafts last 14 days. Ask chat to price it again, or start a new plan.' });
          return;
        }
        let revisesLabel: string | undefined;
        if (doc.revisesPlanId) {
          revisesLabel = await getMediaPlan(doc.revisesPlanId).then(
            ({ plan }) => revisionLabel(plan),
            () => undefined,
          );
          if (cancelled) return;
        }
        const advertiser = advertisersRef.current.find((a) => a.key === doc.request.advertiserKey);
        const draft = draftFromChatDraft(doc, { agencies, advertiserAgencyKey: advertiser?.agencyKey });
        setBoot(bootFor({ draft, revisesPlanId: doc.revisesPlanId, revisesLabel, chatDraft: doc }));
        setChatImport(null);
        onNavigate({ view: 'builder' }, { replace: true });
      } catch (err) {
        if (!cancelled) setChatImport({ id: chatDraftId, error: `Couldn’t open the chat draft: ${errorMessage(err)}` });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Runs once per draft id; the navigation and boot helpers are stable for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatDraftId, referenceLoaded]);

  const planCreated = useCallback(
    (result: CreatePlanResult) => {
      setDetailSeed({ plan: result.plan, agencyLabel: result.agencyLabel, sheet: result.sheet, sheetError: result.sheetError });
      setStored(null);
      onNavigate({ view: 'plan', planId: result.plan.id });
      void loadPlans();
      notify('success', result.sheet ? 'Media plan created — Sheet saved to your Drive' : 'Media plan created');
    },
    [onNavigate, loadPlans, notify],
  );

  const newVersion = useCallback(
    (plan: MediaPlanDoc, agencyLabel?: string) => {
      setBoot(bootFor({ draft: draftFromPlan(plan, { agencyLabel }), revisesPlanId: plan.id, revisesLabel: revisionLabel(plan) }));
      onNavigate({ view: 'builder' });
    },
    [bootFor, onNavigate],
  );

  const planChanged = useCallback((plan: MediaPlanDoc) => {
    setPlans((prev) => prev.map((p) => (p.id === plan.id ? plan : p)));
  }, []);

  const { reference } = registry;
  const registryBanner =
    registry.error && !registry.reference ? (
      <div role="alert" style={{ ...bannerStyle('danger'), marginBottom: 12 }}>
        <span style={{ flex: 1 }}>Could not load the rate cards and advertiser list: {registry.error}</span>
        <Button size="sm" variant="ghost" onClick={registry.reload} disabled={registry.loading}>
          {registry.loading ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    ) : null;

  if (planId) {
    return (
      <PlanDetail
        key={planId}
        planId={planId}
        seed={detailSeed}
        units={reference?.units ?? []}
        canManagePlans={canManagePlans}
        currentUid={uid}
        onBack={() => onNavigate({ view: 'list' })}
        onOpenPlan={(id) => onNavigate({ view: 'plan', planId: id })}
        onNewVersion={newVersion}
        onPlanChanged={planChanged}
        notify={notify}
      />
    );
  }

  if (mode === 'builder') {
    if (chatImport?.error) {
      return (
        <div role="alert" style={bannerStyle('danger')}>
          <span style={{ flex: 1 }}>{chatImport.error}</span>
          <Button size="sm" variant="subtle" onClick={() => onNavigate({ view: 'builder' }, { replace: true })}>
            Start a new plan
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onNavigate({ view: 'list' })}>
            All plans
          </Button>
        </div>
      );
    }
    if (!reference || !boot || chatDraftId) {
      return (
        registryBanner || (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
            <Spinner label={chatDraftId ? 'Opening the chat draft…' : 'Loading the plan builder…'} />
          </div>
        )
      );
    }
    return (
      <PlanBuilder
        key={boot.key}
        boot={boot}
        uid={uid}
        reference={reference}
        advertisers={registry.advertisers}
        canManagePlans={canManagePlans}
        onBack={() => onNavigate({ view: 'list' })}
        onCreated={planCreated}
        onDiscarded={() => {
          setStored(null);
          onNavigate({ view: 'list' });
        }}
        onStartOver={() => {
          clearStoredDraft(uid);
          setBoot({ key: ++bootCount.current, draft: newDraft() });
        }}
        onOpenStored={(saved) => setBoot(restoredBoot(++bootCount.current, saved))}
        onOpenPlan={(id) => onNavigate({ view: 'plan', planId: id })}
        onAdvertiserCreated={registry.upsertAdvertiser}
        onAdvertiserUpdated={registry.upsertAdvertiser}
        notify={notify}
      />
    );
  }

  return (
    <>
      {registryBanner}
      {stored && (
        <div style={{ marginBottom: 12 }}>
          {confirmDiscardStored ? (
            <InlineConfirm
              title="Discard this draft?"
              confirmLabel="Discard draft"
              cancelLabel="Keep it"
              tone="danger"
              onConfirm={() => {
                clearStoredDraft(uid);
                setStored(null);
              }}
              onCancel={() => setConfirmDiscardStored(false)}
            >
              {storedDraftTitle(stored, fmtBudget)} is removed from this browser.
            </InlineConfirm>
          ) : (
            <div role="group" aria-label="Unsaved draft" style={bannerStyle('info')}>
              <span style={{ flex: 1 }}>
                Unsaved draft: {storedDraftTitle(stored, fmtBudget)}
                {stored.revisesLabel ? ` (new version of ${stored.revisesLabel})` : ''} · saved {fmtDateTime(new Date(stored.savedAt).toISOString())}
              </span>
              <Button size="sm" variant="subtle" onClick={() => onNavigate({ view: 'builder' })}>
                Resume
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDiscardStored(true)}>
                Discard
              </Button>
            </div>
          )}
        </div>
      )}
      <PlanList
        plans={plans}
        loading={plansLoading || !active}
        error={plansError}
        loadedAt={plansLoadedAt}
        limit={PLAN_LIST_LIMIT}
        mine={mine}
        currentUid={uid}
        canManagePlans={canManagePlans}
        onMineChange={setMine}
        onOpen={(id) => onNavigate({ view: 'plan', planId: id })}
        onNew={() => onNavigate({ view: 'builder' })}
        onNewVersion={(plan) => newVersion(plan)}
        onRefresh={() => void loadPlans()}
        notify={notify}
      />
    </>
  );
};

export default MediaPlansTab;
